import * as _ from 'lodash';
import moment from 'moment';

const initialState = {
  filterExpandedSections: [],
};

const _getFilterValues = (filterParams, key) =>
  _.compact(
    _.map(_.split(_.toString(_.get(filterParams, `filter.${key}`)), '|'), (value) => {
      try {
        return decodeURIComponent(value);
      } catch (e) {
        return value;
      }
    })
  );

const _getFilterParams = (state) =>
  _.pickBy(state, (value, key) => _.startsWith(key, 'filter.'));

const _transformData = (data) => {
  const processDeep = (rows, parentId = undefined, level = 0, code = '') => {
    return _.map(rows, (row, i) => {
      const id = parentId ? [parentId, row.id].join('-') : _.toString(row.id);
      const new_code = code.length == 0 ? i + 1 : `${code}.${i + 1}`;

      return {
        ...row,
        code: level < 4 ? new_code : undefined,
        level,
        id,
        parentId,
        ownId: row.id,
        children: row.children
          ? processDeep(
              _.sortBy(row.children, 'order'),
              id,
              level + 1,
              new_code
            )
          : [],
      };
    });
  };

  const transformed_data = processDeep(_.sortBy(data, 'order'));

  return transformed_data;
};

const _filterTaskGroups = (rows, selectedIds, parentSelected = false) =>
  _.compact(
    _.map(rows, (row) => {
      const selected =
        parentSelected || _.includes(selectedIds, _.toString(row.ownId));
      const children = selected
        ? row.children || []
        : _filterTaskGroups(row.children || [], selectedIds);

      if (!selected && _.isEmpty(children)) return null;

      return {
        ...row,
        children,
      };
    })
  );

const _filterHierarchy = (data, filterParams) => {
  const stageIds = _getFilterValues(filterParams, 'stage');
  const substageIds = _getFilterValues(filterParams, 'substage');
  const taskGroupIds = _getFilterValues(filterParams, 'task_group');

  return _.compact(
    _.map(data, (stage) => {
      if (
        !_.isEmpty(stageIds) &&
        !_.includes(stageIds, _.toString(stage.ownId))
      ) {
        return null;
      }

      let substages = stage.children || [];

      if (!_.isEmpty(substageIds)) {
        substages = _.filter(substages, (substage) =>
          _.includes(substageIds, _.toString(substage.ownId))
        );
      }

      if (!_.isEmpty(taskGroupIds)) {
        substages = _.compact(
          _.map(substages, (substage) => {
            const children = _filterTaskGroups(
              substage.children || [],
              taskGroupIds
            );

            return _.isEmpty(children)
              ? null
              : {
                  ...substage,
                  children,
                };
          })
        );
      }

      if (
        (!_.isEmpty(substageIds) || !_.isEmpty(taskGroupIds)) &&
        _.isEmpty(substages)
      ) {
        return null;
      }

      return {
        ...stage,
        children: substages,
      };
    })
  );
};

const _matchesDateRange = (row, value) => {
  const [start, end] = _.split(_.toString(value), '|');

  if (!start && !end) return true;

  const rowStart = moment(_.get(row, 'date.start'));
  const rowEnd = moment(_.get(row, 'date.end'));

  if (start && (!rowStart.isValid() || rowStart.isBefore(moment(start), 'day'))) {
    return false;
  }

  if (end && (!rowEnd.isValid() || rowEnd.isAfter(moment(end), 'day'))) {
    return false;
  }

  return true;
};

const _matchesRowFilters = (row, filterParams) => {
  const statuses = _getFilterValues(filterParams, 'status');
  const zones = _getFilterValues(filterParams, 'zone');
  const disciplines = _getFilterValues(filterParams, 'discipline');
  const ragStatuses = _getFilterValues(filterParams, 'rag');
  const dateRange = _.get(filterParams, 'filter.date_range');

  if (
    !_.isEmpty(statuses) &&
    !_.includes(statuses, _.toString(row.status))
  ) {
    return false;
  }

  if (!_.isEmpty(zones)) {
    if (row.level < 2) return false;

    const rowZones = _.isEmpty(row.zones)
      ? ['0']
      : _.map(row.zones, _.toString);

    if (_.isEmpty(_.intersection(zones, rowZones))) return false;
  }

  if (!_.isEmpty(disciplines)) {
    if (row.level < 2) return false;

    const rowDisciplines = _.isEmpty(row.stage_disciplines)
      ? ['0']
      : _.map(row.stage_disciplines, _.toString);

    if (_.isEmpty(_.intersection(disciplines, rowDisciplines))) return false;
  }

  if (
    !_.isEmpty(ragStatuses) &&
    !_.includes(ragStatuses, _.toString(_.get(row, 'performance.color')))
  ) {
    return false;
  }

  return _matchesDateRange(row, dateRange);
};

const _filterRows = (rows, filterParams) =>
  _.compact(
    _.map(rows, (row) => {
      const children = _filterRows(row.children || [], filterParams);

      if (!_matchesRowFilters(row, filterParams) && _.isEmpty(children)) {
        return null;
      }

      return {
        ...row,
        children,
      };
    })
  );

const _filterData = (data, filterParams) => {
  const hierarchyFilteredData = _filterHierarchy(
    _.cloneDeep(data || []),
    filterParams
  );
  const hasRowFilters = _.some(
    ['status', 'zone', 'discipline', 'date_range', 'rag'],
    (key) => !_.isEmpty(_getFilterValues(filterParams, key))
  );

  return hasRowFilters
    ? _filterRows(hierarchyFilteredData, filterParams)
    : hierarchyFilteredData;
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case 'table.stages/SET_TABLE_PARAMS':
      return {
        ...state,
        ...action.params,
      };
    case 'table.stages/LIST_PROJECT_STAGES_SUCCESS':
      return {
        ...state,
        data: action.response.data,
        filters: action.response.filters,
        ...action.response.pagination,
      };
    case 'table.stages/READ_STAGE_SUCCESS':
      return {
        ...state,
        flyout: action.response.data,
      };

    case 'table.stages/LIST_PROJECT_SUBSTAGES_SUCCESS':
    case 'table.stages/LIST_PROJECT_SUBSTAGES_SCHEDULE_SUCCESS': {
      const transformedSubstages = _transformData(action.response.data);

      return {
        ...state,
        substages: _filterData(transformedSubstages, _getFilterParams(state)),
        initialSubstages: transformedSubstages,
      };
    }
    case 'table.stages/LIST_PROJECT_SUBSTAGES_FILTERS_SUCCESS':
      return {
        ...state,
        filters: action.response.filters,
      };
    case 'table.stages/FILTER_PROJECT_SUBSTAGES':
      return {
        ...state,
        substages: _filterData(
          state.initialSubstages || state.substages,
          _getFilterParams(state)
        ),
      };
    default:
      return state;
  }
}
